import React, {createContext, useContext, useEffect, useState} from 'react';

export enum DialogType {
    TOS = 'TOS',
    TRIAL = 'TRIAL',
    PURCHASE = 'PURCHASE',
    WELCOME = 'WELCOME'
}

export const DIALOG_PRIORITIES: Record<DialogType, number> = {
    [DialogType.TOS]: 100,
    [DialogType.TRIAL]: 90,
    [DialogType.PURCHASE]: 90,
    [DialogType.WELCOME]: 80
};

interface QueuedDialog {
    type: DialogType;
    data: any;
}

interface DialogContextType {
    activeDialog: DialogType | null;
    activeDialogData: any | null;
    queuedDialogs: QueuedDialog[];
    openDialog: (type: DialogType, data?: any) => void;
    closeDialog: (type: DialogType) => void;
    isDialogQueued: (type: DialogType) => boolean;
    clearQueuedDialogs: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialogContext = (): DialogContextType => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialogContext must be used within a DialogProvider');
    }
    return context;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [activeDialog, setActiveDialog] = useState<DialogType | null>(null);
    const [activeDialogData, setActiveDialogData] = useState<any | null>(null);
    const [queuedDialogs, setQueuedDialogs] = useState<QueuedDialog[]>([]);

    useEffect(() => {
        const storedQueuedDialogs = sessionStorage.getItem('queuedDialogs');
        if (storedQueuedDialogs) {
            try {
                const parsed = JSON.parse(storedQueuedDialogs) as QueuedDialog[];
                setQueuedDialogs(parsed);
            } catch (e) {
                sessionStorage.removeItem('queuedDialogs');
            }
        }
    }, []);

    useEffect(() => {
        if (queuedDialogs.length > 0) {
            sessionStorage.setItem('queuedDialogs', JSON.stringify(queuedDialogs));
        } else {
            sessionStorage.removeItem('queuedDialogs');
        }
    }, [queuedDialogs]);

    const openDialog = (type: DialogType, data?: any) => {
        const requestedPriority = DIALOG_PRIORITIES[type];

        if (!activeDialog) {
            setActiveDialog(type);
            setActiveDialogData(data || null);
            return;
        }

        const activePriority = DIALOG_PRIORITIES[activeDialog];

        if (requestedPriority > activePriority) {
            setQueuedDialogs(prev => [...prev, {type: activeDialog, data: activeDialogData}]);
            setActiveDialog(type);
            setActiveDialogData(data || null);
        } else {
            setQueuedDialogs(prev => [...prev, {type, data: data || null}]);
        }
    };

    const closeDialog = (type: DialogType) => {
        // If the dialog is active, close it
        if (activeDialog === type) {
            setActiveDialog(null);
            setActiveDialogData(null);

            // Show next queued dialog with highest priority
            if (queuedDialogs.length > 0) {
                const sortedQueued = [...queuedDialogs].sort((a, b) =>
                    DIALOG_PRIORITIES[b.type] - DIALOG_PRIORITIES[a.type]
                );
                const nextDialog = sortedQueued[0];
                setQueuedDialogs(prev => prev.filter(d => d !== nextDialog));
                setActiveDialog(nextDialog.type);
                setActiveDialogData(nextDialog.data);
            }
        } else {
            // If the dialog is queued, remove it from the queue
            setQueuedDialogs(prev => prev.filter(d => d.type !== type));
        }
    };

    const isDialogQueued = (type: DialogType): boolean => {
        return queuedDialogs.some(d => d.type === type);
    };

    const clearQueuedDialogs = () => {
        setQueuedDialogs([]);
    };

    return (
        <DialogContext.Provider
            value={{
                activeDialog,
                activeDialogData,
                queuedDialogs,
                openDialog,
                closeDialog,
                isDialogQueued,
                clearQueuedDialogs
            }}
        >
            {children}
        </DialogContext.Provider>
    );
};
